import { addMonths, differenceInCalendarDays, endOfMonth, format, getDaysInMonth, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { isScheduledColleague } from './vacationClientPanel.definitions';
import type { ClientVacationPeriod } from '../../models/vacationClientView.types';
import type { VacationRequestStatus } from '../../models/vacation.types';

export function colleagueRowKey(collaboratorId: string, status: VacationRequestStatus): string {
    const isScheduled = isScheduledColleague(status);
    const isOnVacation = status === 'OnVacation';
    const statusKey = isOnVacation ? 'onVacation' : isScheduled ? 'scheduled' : 'pending';
    return `${statusKey}:${collaboratorId}`;
}

export type CalendarBarTone = 'self' | 'overlap' | 'nearby' | 'scheduled' | 'onVacation';

export interface CalendarRange {
    start: Date;
    end: Date;
}

export interface CalendarBar extends CalendarRange {
    leftPct: number;
    widthPct: number;
}

export interface CalendarRow {
    id: string;
    collaboratorId: string;
    name: string;
    tone: CalendarBarTone;
    bars: CalendarBar[];
}

export interface CalendarMonth {
    key: string;
    label: string;
    leftPct: number;
    widthPct: number;
}

export interface CalendarTick {
    key: string;
    label: string;
    leftPct: number;
}

export interface ConflictCalendarModel {
    months: CalendarMonth[];
    ticks: CalendarTick[];
    selfBars: CalendarBar[];
    rows: CalendarRow[];
}

const DENSE_TICK_DAYS = [1, 5, 10, 15, 20, 25];
const SPARSE_TICK_DAYS = [1, 15];
const DENSE_TICK_MAX_MONTHS = 3;

const TONE_RANK: Record<'overlap' | 'nearby' | 'scheduled' | 'onVacation', number> = {
    overlap: 0,
    nearby: 1,
    onVacation: 2,
    scheduled: 3,
};

interface ColleagueEntry {
    collaboratorId: string;
    name: string;
    tone: 'overlap' | 'nearby' | 'scheduled' | 'onVacation';
    ranges: Map<string, CalendarRange>;
}

function rangesIntersect(a: CalendarRange, b: CalendarRange): boolean {
    return a.start <= b.end && b.start <= a.end;
}

function capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function addRange(entry: ColleagueEntry, range: CalendarRange): void {
    entry.ranges.set(`${range.start.getTime()}_${range.end.getTime()}`, range);
}

function collectColleagues(period: ClientVacationPeriod): Map<string, ColleagueEntry> {
    const colleagues = new Map<string, ColleagueEntry>();

    period.overlaps.forEach((overlap) => {
        const isScheduled = isScheduledColleague(overlap.colleagueStatus);
        const isOnVacation = overlap.colleagueStatus === 'OnVacation';
        const groupKey = colleagueRowKey(overlap.collaboratorId, overlap.colleagueStatus);

        const entry = colleagues.get(groupKey) ?? {
            collaboratorId: overlap.collaboratorId,
            name: overlap.collaboratorName,
            tone: isScheduled ? (isOnVacation ? 'onVacation' : 'scheduled') : ('nearby' as const),
            ranges: new Map<string, CalendarRange>(),
        };
        if (!isScheduled && overlap.proximity === 'overlap') entry.tone = 'overlap';
        addRange(entry, { start: overlap.colleagueStart, end: overlap.colleagueEnd });
        colleagues.set(groupKey, entry);
    });

    return colleagues;
}

function earliest(ranges: CalendarRange[]): number {
    return Math.min(...ranges.map((range) => range.start.getTime()));
}

export function buildConflictCalendar(
    period: ClientVacationPeriod,
    siblingPeriods: ClientVacationPeriod[] = []
): ConflictCalendarModel {
    const selfRanges: CalendarRange[] = period.segments.map((segment) => ({
        start: segment.startDate,
        end: segment.endDate,
    }));
    const colleagues = collectColleagues(period);
    const allRanges = [
        ...selfRanges,
        ...Array.from(colleagues.values()).flatMap((entry) => Array.from(entry.ranges.values())),
    ];

    const rangeStart = startOfMonth(new Date(Math.min(...allRanges.map((r) => r.start.getTime()))));
    const rangeEnd = endOfMonth(new Date(Math.max(...allRanges.map((r) => r.end.getTime()))));
    const totalDays = differenceInCalendarDays(rangeEnd, rangeStart) + 1;
    const window: CalendarRange = { start: rangeStart, end: rangeEnd };

    const toPct = (date: Date) => (differenceInCalendarDays(date, rangeStart) / totalDays) * 100;
    const toBar = (range: CalendarRange): CalendarBar => ({
        ...range,
        leftPct: toPct(range.start),
        widthPct: ((differenceInCalendarDays(range.end, range.start) + 1) / totalDays) * 100,
    });

    const months: CalendarMonth[] = [];
    for (let cursor = rangeStart; cursor <= rangeEnd; cursor = addMonths(cursor, 1)) {
        months.push({
            key: format(cursor, 'yyyy-MM'),
            label: capitalize(format(cursor, 'MMMM yyyy', { locale: ptBR })),
            leftPct: toPct(cursor),
            widthPct: (getDaysInMonth(cursor) / totalDays) * 100,
        });
    }

    const tickDays = months.length <= DENSE_TICK_MAX_MONTHS ? DENSE_TICK_DAYS : SPARSE_TICK_DAYS;
    const ticks: CalendarTick[] = months.flatMap((month, index) => {
        const monthStart = addMonths(rangeStart, index);
        return tickDays.map((day) => {
            const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
            return {
                key: `${month.key}-${day}`,
                label: String(day).padStart(2, '0'),
                leftPct: toPct(date),
            };
        });
    });

    siblingPeriods.forEach((sibling) => {
        if (sibling.collaboratorId === period.collaboratorId) return;
        if (!isScheduledColleague(sibling.status)) return;

        const rangesInWindow = sibling.segments
            .map((segment) => ({ start: segment.startDate, end: segment.endDate }))
            .filter((range) => rangesIntersect(range, window));
        if (rangesInWindow.length === 0) return;

        const isOnVacation = sibling.status === 'OnVacation';
        const tone: 'onVacation' | 'scheduled' = isOnVacation ? 'onVacation' : 'scheduled';
        const groupKey = colleagueRowKey(sibling.collaboratorId, sibling.status);
        const entry = colleagues.get(groupKey) ?? {
            collaboratorId: sibling.collaboratorId,
            name: sibling.collaboratorName,
            tone,
            ranges: new Map<string, CalendarRange>(),
        };
        rangesInWindow.forEach((range) => addRange(entry, range));
        colleagues.set(groupKey, entry);
    });

    const colleagueRows: CalendarRow[] = Array.from(colleagues.entries())
        .map(([groupKey, entry]) => ({
            id: groupKey,
            collaboratorId: entry.collaboratorId,
            name: entry.name,
            tone: entry.tone,
            bars: Array.from(entry.ranges.values()).map(toBar),
        }))
        .sort((a, b) => {
            if (a.tone !== b.tone) return TONE_RANK[a.tone] - TONE_RANK[b.tone];
            return earliest(a.bars) - earliest(b.bars);
        });

    const selfBars = selfRanges.map(toBar);

    return {
        months,
        ticks,
        selfBars,
        rows: [
            { id: 'self', collaboratorId: period.collaboratorId, name: period.collaboratorName, tone: 'self', bars: selfBars },
            ...colleagueRows,
        ],
    };
}
